import { useState, useRef, useEffect } from "react";

/* model-viewer (three.js, ~300 KB gzip) loades LAZY: først efter window.load
   OG et idle-vindue, så hverken LCP eller TBT betaler for forsidens 3D.
   Emoji-fallbacken vises, til web-komponenten er klar (mekanismen fandtes i
   forvejen), og selve GLB'erne hentes med loading="lazy" (kun nær viewporten). */
let mvPromise = null;
const loadModelViewer = () => {
  if (!mvPromise) {
    mvPromise = new Promise((resolve) => {
      const kick = () => {
        const idle = window.requestIdleCallback || ((fn) => setTimeout(fn, 600));
        idle(() => resolve(import("@google/model-viewer")), { timeout: 2500 });
      };
      if (document.readyState === "complete") kick();
      else window.addEventListener("load", kick, { once: true });
    });
  }
  return mvPromise;
};
const mvReady = () =>
  typeof customElements !== "undefined" && !!customElements.get("model-viewer");

/* A real 3D model (GLB), shown with no ground shadow.
   - interactive (default): drag to rotate + gentle idle auto-spin.
   - static: a fixed 3D pose, no rotation (for decorative icons).
   `orientation` rotates the model itself (e.g. lay an upright model into side
   profile); `orbit` locks the camera angle. Both are applied as ATTRIBUTES via
   a ref — React sets them as properties, which model-viewer ignores.
   `thumb` (PNG-render af modellen) vises i stedet for emoji-fallbacken før
   opgradering/ved fejl OG som model-viewer-poster, mens GLB'en henter —
   forsiden viser dermed altid egen grafik, aldrig emojis. */
export default function Model3D({ src, fallback, thumb, size = 78, static: isStatic = false, animated = false, spinOnHover = false, orbit, orientation, target, spinDeg = 26, orbitLock = true }) {
  const [failed, setFailed] = useState(false);
  const [ready, setReady] = useState(mvReady());
  const [inView, setInView] = useState(false);
  const ref = useRef(null);
  const hostRef = useRef(null);

  /* kun modeller nær viewporten opgraderes — resten forbliver emoji, til man
     scroller derned (halverer main-thread-arbejdet ved load på mobilen) */
  useEffect(() => {
    if (inView) return;
    const el = hostRef.current;
    if (!el || !("IntersectionObserver" in window)) { setInView(true); return; }
    const io = new IntersectionObserver(
      (entries) => { if (entries.some((e) => e.isIntersecting)) { setInView(true); io.disconnect(); } },
      { rootMargin: "140px" }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [inView]);

  useEffect(() => {
    if (ready || !inView) return;
    let alive = true;
    loadModelViewer()
      .then(() => { if (alive) setReady(true); })
      .catch(() => { if (alive) setFailed(true); });
    return () => { alive = false; };
  }, [ready, inView]);

  useEffect(() => {
    const m = ref.current;
    if (!m) return;
    // model-viewer only applies these reliably once the model has loaded —
    // and React sets them as (falsy) properties, so force real attributes
    const apply = () => {
      if (orientation) m.setAttribute("orientation", orientation);
      if (target) m.setAttribute("camera-target", target);   // e.g. centre on a sub-part of a composite
      if (orbit) {
        m.setAttribute("camera-orbit", orbit);
        // orbitLock=false: orbit er kun STARTvinklen (fx basketballens tekst
        // forfra) — brugeren kan stadig dreje frit
        if (orbitLock) {
          m.setAttribute("min-camera-orbit", orbit);
          m.setAttribute("max-camera-orbit", orbit);
        }
      }
      if (animated) {
        m.setAttribute("autoplay", "");
        if (typeof m.play === "function") { try { m.play(); } catch {} }
      }
      if ((orbit || target) && typeof m.jumpCameraToGoal === "function") { try { m.jumpCameraToGoal(); } catch {} }
    };
    apply();
    if (m.loaded) apply();
    else m.addEventListener("load", apply, { once: true });
    // React doesn't wire on-props as listeners on custom elements — do it here
    const onErr = () => setFailed(true);
    m.addEventListener("error", onErr);

    // hover = spin (mouse); a tap gives a short spin (touch has no hover)
    let touchTimer = null;
    const play = () => { try { m.play(); } catch {} };
    const pause = () => { try { m.pause(); } catch {} };
    const onTouch = () => {
      play();
      clearTimeout(touchTimer);
      touchTimer = setTimeout(pause, 2600);
    };
    if (spinOnHover) {
      m.addEventListener("mouseenter", play);
      m.addEventListener("mouseleave", pause);
      m.addEventListener("touchstart", onTouch, { passive: true });
    }
    return () => {
      m.removeEventListener("load", apply);
      m.removeEventListener("error", onErr);
      if (spinOnHover) {
        m.removeEventListener("mouseenter", play);
        m.removeEventListener("mouseleave", pause);
        m.removeEventListener("touchstart", onTouch);
        clearTimeout(touchTimer);
      }
    };
  }, [orbit, orientation, target, animated, spinOnHover, failed, ready, orbitLock]);

  const placeholder = thumb
    ? <img src={thumb} alt="" draggable={false} loading="lazy"
        style={{ width: size + "px", height: size + "px", objectFit: "contain" }} />
    : fallback;
  if (failed) return <span className="cv-temoji">{placeholder}</span>;
  if (!ready || !inView) return <span ref={hostRef} className="cv-temoji">{placeholder}</span>;

  // spinDeg: grader/sekund for idle-rotationen; NEGATIV = mod venstre
  // (bruges af basketballen, så branding-teksten kan læses forfra)
  const spin = isStatic
    ? {}
    : { "camera-controls": "", "auto-rotate": "", "auto-rotate-delay": "0", "rotation-per-second": `${spinDeg}deg` };
  // animated GLBs (e.g. the waving teddy) loop their first clip;
  // the autoplay attribute itself is applied via the ref (React sets falsy props)
  const anim = {};

  return (
    <model-viewer
      ref={ref}
      src={src}
      alt=""
      poster={thumb || undefined}
      {...spin}
      {...anim}
      loading="lazy"
      disable-zoom=""
      disable-pan=""
      interaction-prompt="none"
      shadow-intensity="0"
      exposure="1.1"
      environment-image="neutral"
      style={{
        width: size + "px", height: size + "px",
        background: "transparent", "--poster-color": "transparent",
      }}
    >
      {/* empty slot hides model-viewer's built-in gray progress bar */}
      <div slot="progress-bar" />
    </model-viewer>
  );
}
